/**
 * Wedding Books — shared formatting, status vocabulary and date maths.
 *
 * NO HEX ANYWHERE. Everything colour-shaped here is a MUI palette KEY
 * ('success' | 'warning' | …) or a named brandColors constant, so both themes
 * render correctly and dark mode needs no second definition.
 */

import { brandColors } from '../../theme/brandColors';
import type { BookStatus, GuestStatus, Numeric, Tier } from './weddingApi';

/** node-pg returns bigint/numeric as strings — coerce before any arithmetic. */
export function num(value: Numeric | null | undefined): number {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  return typeof n === 'number' && Number.isFinite(n) ? n : 0;
}

/** Cents → "$1,234.00". Whole dollars above $10k to keep KPI cards readable. */
export function centsToUsd(cents: Numeric | null | undefined): string {
  const dollars = num(cents) / 100;
  if (Math.abs(dollars) >= 10000) {
    return `$${dollars.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
  return `$${dollars.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Cents → "$1,499" for a KPI TILE. Whole dollars, always.
 *
 * `centsToUsd` already drops the cents above $10,000 "to keep KPI cards
 * readable", and the instinct was right — the threshold was just measured on
 * nothing. A quarter-width MetricCard at 1440px gives its value about 180px in
 * the 48px display serif, which is eight characters. `$1,499.00` is nine, so a
 * real book's revenue wrapped to a second line and rendered as "$1,499.0" over
 * "0". Found in the first browser render of the Revenue page (session I).
 *
 * A tile is read at a glance and dollars are the unit anyone reads it in; the
 * cents are never lost, because the exact figure is always in the card's own
 * subtitle or in the purchases table beneath it. Use `centsToUsd` — which keeps
 * them — for anything that is a transaction rather than a headline.
 */
export function centsToUsdTile(cents: Numeric | null | undefined): string {
  const dollars = num(cents) / 100;
  return `$${Math.round(dollars).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

/** Percentage of a whole, rounded, guarded against a zero denominator. */
export function percentage(part: Numeric | null | undefined, whole: Numeric | null | undefined): number {
  const w = num(whole);
  if (w <= 0) return 0;
  return Math.round((num(part) / w) * 100);
}

// ── Status vocabulary ───────────────────────────────────────────────────────

export type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

export interface StatusMeta {
  label: string;
  color: ChipColor;
  description: string;
}

/**
 * The eight book statuses from migration 438. Kept as its own vocabulary rather
 * than folded into the shared StatusBadge component, whose words
 * (active/inactive/pending/…) do not describe a wedding book's lifecycle.
 */
export const STATUS_META: Record<BookStatus, StatusMeta> = {
  draft: {
    label: 'Draft',
    color: 'default',
    description: 'Created but not paid for. The couple is still in the setup wizard.',
  },
  pending_payment: {
    label: 'Pending payment',
    color: 'warning',
    description: 'Checkout started and not completed. Nobody can be invited yet.',
  },
  active: {
    label: 'Active',
    color: 'info',
    description: 'Paid for, but no invitation has gone out yet.',
  },
  collecting: {
    label: 'Collecting',
    color: 'success',
    description: 'Guests have been invited and stories are coming in.',
  },
  closed: {
    label: 'Closed',
    color: 'default',
    description: 'The deadline has passed. No new stories are accepted.',
  },
  generating: {
    label: 'Generating',
    color: 'info',
    description: 'The book PDF is being produced.',
  },
  delivered: {
    label: 'Delivered',
    color: 'info',
    description: 'The finished book has been handed to the couple.',
  },
  suspended: {
    label: 'Suspended',
    color: 'error',
    description: 'Paused by the LifeScribe team. Guests cannot contribute.',
  },
};

export const BOOK_STATUSES = Object.keys(STATUS_META) as BookStatus[];

/** Funnel order for the status bar — lifecycle order, suspended called out separately. */
export const STATUS_FUNNEL_ORDER: BookStatus[] = [
  'draft', 'pending_payment', 'active', 'collecting', 'closed', 'generating', 'delivered',
];

export interface TierMeta {
  label: string;
  /** MUI Chip variant + a brand accent for the two premium tiers. */
  variant: 'outlined' | 'filled';
  accent: 'neutral' | 'teal' | 'gold';
}

export const TIER_META: Record<Tier, TierMeta> = {
  classic: { label: 'Classic', variant: 'outlined', accent: 'neutral' },
  premium: { label: 'Premium', variant: 'outlined', accent: 'teal' },
  luxury: { label: 'Luxury', variant: 'outlined', accent: 'gold' },
  platinum: { label: 'Platinum', variant: 'filled', accent: 'gold' },
};

export const TIERS = Object.keys(TIER_META) as Tier[];

/** Named brand constants, never a literal hex in a component. */
export const TIER_ACCENT_COLOR: Record<TierMeta['accent'], string | undefined> = {
  neutral: undefined,
  teal: brandColors.deepTeal,
  gold: brandColors.warmGold,
};

export const GUEST_STATUS_META: Record<GuestStatus, { label: string; color: ChipColor }> = {
  pending: { label: 'Not invited', color: 'default' },
  invited: { label: 'Invited', color: 'info' },
  verified: { label: 'Signed in', color: 'info' },
  writing: { label: 'Writing', color: 'warning' },
  submitted: { label: 'Submitted', color: 'success' },
};

/**
 * Copy for the activity feed. Mirrors lifescribe-wedding-web's activityCopy.
 *
 * The keys are the sixteen values migration 455 allows on
 * `wedding_activity_events.event_type`. An event type absent from this map
 * still renders (as "something happened") rather than blanking a row.
 */
export const ACTIVITY_COPY: Record<string, string> = {
  guest_invited: 'was invited to contribute',
  guest_verified: 'confirmed their email',
  chapter_started: 'started writing',
  chapter_submitted: 'sent in their chapter',
  photos_added: 'added photographs',
  voice_added: 'recorded their voice',
  video_added: 'sent a video message',
  reminder_sent: 'was sent a reminder',
  book_activated: 'the book opened for chapters',
  deadline_extended: 'the chapter deadline moved',
  book_suspended: 'the book was suspended',
  book_reactivated: 'the book was reactivated',
  invite_link_clicked: 'opened their invitation link',
  gateway_web_continued: 'continued on the web',
  gateway_app_opened: 'opened the app',
  tier_changed: 'the book changed tier',
};

export function activityLabel(eventType: string, actorName: string | null): string {
  const phrase = ACTIVITY_COPY[eventType] || 'something happened';
  // Book-level events read as sentences on their own; guest events need a name.
  if (eventType.startsWith('book_') || eventType === 'deadline_extended' || eventType === 'tier_changed') {
    return phrase.charAt(0).toUpperCase() + phrase.slice(1);
  }
  return `${actorName || 'Someone'} ${phrase}`;
}

/**
 * The colour of an activity feed dot — a MUI PALETTE KEY, never a hex.
 *
 * Four families, so a feed can be read at a glance without reading it:
 *   · money moving        → 'success' (forest green)
 *   · a guest contributing → 'primary' (deep teal)
 *   · something being sent → 'secondary' (warm gold)
 *   · a guest becoming a member, or a book being stopped → 'error' (burgundy)
 *
 * Anything unrecognised falls back to 'primary'. A missing colour must not be a
 * missing dot: the row still has to line up with the ones around it.
 */
export type ActivityTone = 'primary' | 'secondary' | 'success' | 'error';

export const ACTIVITY_TONE: Record<string, ActivityTone> = {
  // Money.
  tier_changed: 'success',
  book_activated: 'success',
  // Contribution.
  chapter_started: 'primary',
  chapter_submitted: 'primary',
  photos_added: 'primary',
  voice_added: 'primary',
  video_added: 'primary',
  guest_verified: 'primary',
  // Sends.
  guest_invited: 'secondary',
  reminder_sent: 'secondary',
  invite_link_clicked: 'secondary',
  // Conversion, and the one intervention that is not good news.
  gateway_web_continued: 'error',
  gateway_app_opened: 'error',
  book_suspended: 'error',
};

export function activityTone(eventType: string): ActivityTone {
  return ACTIVITY_TONE[eventType] || 'primary';
}

// ── Dates and countdowns ────────────────────────────────────────────────────

/**
 * The calendar date an instant falls on IN A GIVEN TIMEZONE, as 'YYYY-MM-DD'.
 * Copied from lifescribe-wedding-book/src/services/dashboardService.js so an
 * operator's countdown matches the couple's to the day. A bad stored timezone
 * falls back to UTC rather than throwing.
 */
export function calendarDateInZone(instant: string | Date | null | undefined, timeZone?: string): string | null {
  if (!instant) return null;
  const date = instant instanceof Date ? instant : new Date(instant);
  if (Number.isNaN(date.getTime())) return null;
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timeZone || 'UTC', year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'UTC', year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(date);
  }
}

/** Whole days between two 'YYYY-MM-DD' calendar dates. Negative means past. */
export function daysBetweenDates(from: string | null, to: string | null): number | null {
  if (!from || !to) return null;
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / 86400000);
}

export interface Countdown {
  days: number | null;
  label: string;
  overdue: boolean;
}

/**
 * "in 12 days" / "today" / "3 days ago", counted in calendar days in the
 * COUPLE's timezone — the same semantics the couple's own dashboard uses.
 */
export function deadlineCountdown(
  deadline: string | null | undefined,
  timeZone: string | undefined,
  now: Date = new Date()
): Countdown {
  if (!deadline) return { days: null, label: 'No deadline set', overdue: false };
  const today = calendarDateInZone(now, timeZone);
  const then = calendarDateInZone(deadline, timeZone);
  const days = daysBetweenDates(today, then);
  if (days === null) return { days: null, label: 'No deadline set', overdue: false };
  if (days === 0) return { days: 0, label: 'Today', overdue: false };
  if (days > 0) return { days, label: `in ${days} day${days === 1 ? '' : 's'}`, overdue: false };
  const past = Math.abs(days);
  return { days, label: `${past} day${past === 1 ? '' : 's'} overdue`, overdue: true };
}

/** Short absolute date, e.g. "14 Jun 2027". Locale-formatted, never parsed back. */
export function shortDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Date + time in a named timezone, for anything the couple would read. */
export function dateTimeInZone(value: string | null | undefined, timeZone?: string): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: timeZone || 'UTC',
      day: 'numeric', month: 'short', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    }).format(d);
  } catch {
    return d.toISOString();
  }
}

/** "3 minutes ago" / "2 days ago", for the activity feed. */
export function relativeTime(value: string | null | undefined, now: Date = new Date()): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const seconds = Math.round((now.getTime() - d.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return shortDate(value);
}

/**
 * An instant → the 'YYYY-MM-DDTHH:mm' string a <input type="datetime-local">
 * expects, rendered IN THE COUPLE'S TIMEZONE. Without the zone shift the field
 * would pre-fill with the operator's local wall clock and an unwitting "save"
 * would move the deadline by the offset.
 */
export function toDatetimeLocalInZone(value: string | null | undefined, timeZone?: string): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timeZone || 'UTC',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(d);
    const get = (type: string) => parts.find((p) => p.type === type)?.value || '';
    // Intl renders midnight as '24' in some ICU versions; normalise it.
    const hour = get('hour') === '24' ? '00' : get('hour');
    return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`;
  } catch {
    return '';
  }
}

/**
 * Is a 'YYYY-MM-DDTHH:mm' wall-clock value in the past IN A GIVEN ZONE?
 * Client-side courtesy only — the server re-validates the same rule in SQL.
 */
export function isLocalDateTimeInPast(local: string, timeZone?: string, now: Date = new Date()): boolean {
  if (!local) return true;
  const nowLocal = toDatetimeLocalInZone(now.toISOString(), timeZone);
  if (!nowLocal) return false;
  return local <= nowLocal;
}
